import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    CircularProgress,
    Alert,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Autocomplete,
    Pagination,
} from '@mui/material';
import {
    Laptop,
    Smartphone,
    Wifi,
    Cpu,
    Monitor,
    HardDrive,
    Tablet,
    Watch,
    Plus,
    Filter,
    Download,
    Edit,
    Trash2
} from 'lucide-react';


const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const assetTypeIcons = {
    'Laptop': <Laptop size={24} />,
    'Mobile Phone': <Smartphone size={24} />,
    'Data Card': <Wifi size={24} />,
    'Sim Card': <Cpu size={24} />,
    'Monitor': <Monitor size={24} />,
    'Storage': <HardDrive size={24} />,
    'Tablet': <Tablet size={24} />,
    'Smart Watch': <Watch size={24} />,
    'Other': <Plus size={24} />
};

const AssetManagement = () => {
    const [allAssets, setAllAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const [typeFilter, setTypeFilter] = useState("All");
    const [stats, setStats] = useState({ total: 0, issued: 0, returned: 0, damaged: 0 });
    const itemsPerPage = 10;

    // Read role robustly: try localStorage 'role' key first, then JWT token, then user object
    const userRole = (() => {
        const direct = localStorage.getItem('role')?.toLowerCase();
        if (direct) return direct;
        try {
            const token = localStorage.getItem('token');
            if (token) {
                const payload = JSON.parse(atob(token.split('.')[1]));
                if (payload.role) return payload.role.toLowerCase();
            }
            const user = JSON.parse(localStorage.getItem('user') || 'null');
            if (user?.role) return user.role.toLowerCase();
        } catch (_) { }
        return '';
    })();

    const isAdmin = ['hr', 'admin'].includes(userRole);

    // HR Management State
    const [openHRDialog, setOpenHRDialog] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [allCostCenters, setAllCostCenters] = useState([]);
    const [employeeCostCenters, setEmployeeCostCenters] = useState([]);
    const [hrFormData, setHRFormData] = useState({
        employee_id: '',
        assetName: '',
        assetType: 'Laptop',
        serialNumber: '',
        model: '',
        issuedDate: new Date().toISOString().split('T')[0],
        status: 'Issued',
        costCenterId: ''
    });
    const [editingAsset, setEditingAsset] = useState(null);

    const [primaryCostCenterId, setPrimaryCostCenterId] = useState(null);

    const fetchCostCenters = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            const response = await axios.get(`${API_URL}/api/cost-centers`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setAllCostCenters(response.data.data || []);
            }
        } catch (err) {
        }
    };

    const fetchEmployeeCostCenters = async (empId) => {
        if (!empId) {
            setEmployeeCostCenters([]);
            setPrimaryCostCenterId(null);
            return;
        }
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/api/assets/employee-cost-centers/${empId}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            if (response.data.success) {
                const assigned = response.data.data || [];
                const primaryId = response.data.primaryCostCenterId || null;
                setEmployeeCostCenters(assigned);
                setPrimaryCostCenterId(primaryId);
                if (assigned.length === 1) {
                    setHRFormData(prev => ({ ...prev, costCenterId: assigned[0].id }));
                } else if (primaryId) {
                    setHRFormData(prev => ({ ...prev, costCenterId: primaryId }));
                }
            }
        } catch (err) {
        }
    };

    const fetchEmployees = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            const payload = JSON.parse(atob(token.split('.')[1]));
            const company_id = payload.company_id;

            const response = await axios.get(`${API_URL}/api/viewpersonal?limit=1000`, {
                headers: { Authorization: `Bearer ${token}` },
                
            });
            if (response.data.success) {
                setEmployees(response.data.data);
            }
        } catch (err) {
        }
    };

    const fetchAllAssets = async () => {
        try {
            const token = localStorage.getItem('token');
            setLoading(true);
            const response = await axios.get(
                `${API_URL}/api/assets?page=${page}&limit=${itemsPerPage}&search=${encodeURIComponent(searchTerm)}&status=${statusFilter}&type=${typeFilter}`, 
                {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    withCredentials: true
                }
            );
            if (response.data.success) {
                setAllAssets(response.data.data || []);
                setTotalPages(response.data.pages || 1);
                if (response.data.stats) {
                    setStats(response.data.stats);
                } else {
                    const dataArr = response.data.data || [];
                    setStats({
                        total: dataArr.length,
                        issued: dataArr.filter(a => a.status === 'Issued').length,
                        returned: dataArr.filter(a => a.status === 'Returned').length,
                        damaged: dataArr.filter(a => a.status === 'Damaged').length
                    });
                }
            }
        } catch (err) {
            setError('Failed to load organization assets');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isAdmin) {
            fetchAllAssets();
        }
    }, [isAdmin, page, searchTerm, statusFilter, typeFilter]);

    useEffect(() => {
        if (isAdmin) {
            fetchEmployees();
            fetchCostCenters();
        } else {
            setLoading(false);
        }
    }, [isAdmin]);

    const handleOpenHRDialog = () => setOpenHRDialog(true);
    const handleCloseHRDialog = () => {
        setOpenHRDialog(false);
        setEditingAsset(null);
        setEmployeeCostCenters([]);
        setHRFormData({
            employee_id: '',
            assetName: '',
            assetType: 'Laptop',
            serialNumber: '',
            model: '',
            issuedDate: new Date().toISOString().split('T')[0],
            status: 'Issued',
            costCenterId: ''
        });
    };

    const handleHRInputChange = (e) => {
        const { name, value } = e.target;
        setHRFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleHRSubmit = async () => {
        if (!hrFormData.employee_id || !hrFormData.assetName) {
            alert('Please fill in all required fields');
            return;
        }
        try {
            const endpoint = editingAsset ? `${API_URL}/api/assets/${editingAsset.id}` : `${API_URL}/api/assets`;
            const method = editingAsset ? 'put' : 'post';

            const token = localStorage.getItem('token');
            const response = await axios[method](endpoint, hrFormData, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                withCredentials: true
            });
            if (response.data.success) {
                fetchAllAssets();
                handleCloseHRDialog();
                setHRFormData({
                    employee_id: '',
                    assetName: '',
                    assetType: 'Laptop',
                    serialNumber: '',
                    model: '',
                    issuedDate: new Date().toISOString().split('T')[0],
                    status: 'Issued'
                });
                setEditingAsset(null);
            }
        } catch (err) {
            alert('Failed to save asset');
        }
    };

    const handleEdit = (asset) => {
        setEditingAsset(asset);
        // issuedDate may be DATEONLY ('2024-01-15') or ISO ('2024-01-15T00:00:00.000Z')
        const issuedDateStr = asset.issuedDate
            ? asset.issuedDate.includes('T')
                ? asset.issuedDate.split('T')[0]
                : asset.issuedDate
            : new Date().toISOString().split('T')[0];
        setHRFormData({
            employee_id: asset.employee_id,
            assetName: asset.assetName,
            assetType: asset.assetType,
            serialNumber: asset.serialNumber || '',
            model: asset.model || '',
            issuedDate: issuedDateStr,
            status: asset.status,
            costCenterId: asset.costCenterId || ''
        });
        if (asset.employee_id) {
            fetchEmployeeCostCenters(asset.employee_id);
        }
        setOpenHRDialog(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this asset?')) return;
        try {
            const token = localStorage.getItem('token');
            const response = await axios.delete(`${API_URL}/api/assets/${id}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                withCredentials: true
            });
            if (response.data.success) {
                fetchAllAssets();
            }
        } catch (err) {
            alert('Failed to delete asset');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Issued': return 'primary';
            case 'Returned': return 'success';
            case 'Damaged': return 'error';
            default: return 'default';
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}><CircularProgress /></Box>;

    const activeCostCentersList = employeeCostCenters.length > 0 ? employeeCostCenters : allCostCenters;

    return (
        <Box sx={{ p: 4,  minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    Asset Tracker
                </Typography>
                {isAdmin && (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="contained"
                            startIcon={<Plus size={18} />}
                            onClick={handleOpenHRDialog}
                            sx={{ borderRadius: 2, bgcolor: '#1a237e' }}
                        >
                            Add New Asset
                        </Button>
                       
                    </Box>
                )}
            </Box>

            {/* HR Add/Edit Asset Dialog */}
            <Dialog open={openHRDialog} onClose={handleCloseHRDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: '#1a237e', color: 'white', mb: 2 }}>
                    {editingAsset ? 'Edit Asset Assignment' : 'Add New Asset Assignment'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Autocomplete
                                options={employees}
                                getOptionLabel={(option) => {
                                    const first = (option.firstName && option.firstName !== 'null') ? option.firstName : '';
                                    const last = (option.lastName && option.lastName !== 'null') ? option.lastName : '';
                                    const name = [first, last].filter(Boolean).join(' ');
                                    return `${name} (${option.employee_id})`;
                                }}
                                value={employees.find(e => e.employee_id === hrFormData.employee_id) || null}
                                onChange={(e, newValue) => {
                                    const empId = newValue ? newValue.employee_id : '';
                                    setHRFormData(prev => ({ ...prev, employee_id: empId, costCenterId: '' }));
                                    fetchEmployeeCostCenters(empId);
                                }}
                                renderInput={(params) => <TextField {...params} label="Select Employee" required fullWidth />}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                select
                                label="Cost Center (Budget Source)"
                                name="costCenterId"
                                value={hrFormData.costCenterId || ''}
                                onChange={handleHRInputChange}
                                helperText={
                                    employeeCostCenters.length > 1
                                        ? `💡 Employee is assigned to ${employeeCostCenters.length} Cost Centers. Select which budget funds this asset.`
                                        : employeeCostCenters.length === 1
                                            ? `✓ Auto-selected from Employee's assigned Cost Center`
                                            : `Select Cost Center funding this asset`
                                }
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {activeCostCentersList.map((cc) => (
                                    <MenuItem key={cc.id} value={cc.id}>
                                        {cc.name} ({cc.code})
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Asset Name"
                                name="assetName"
                                value={hrFormData.assetName}
                                onChange={handleHRInputChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                select
                                label="Asset Type"
                                name="assetType"
                                value={hrFormData.assetType}
                                onChange={handleHRInputChange}
                            >
                                {Object.keys(assetTypeIcons).map(type => (
                                    <MenuItem key={type} value={type}>{type}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                select
                                label="Status"
                                name="status"
                                value={hrFormData.status}
                                onChange={handleHRInputChange}
                            >
                                <MenuItem value="Issued">Issued</MenuItem>
                                <MenuItem value="Returned">Returned</MenuItem>
                                <MenuItem value="Damaged">Damaged</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Model"
                                name="model"
                                value={hrFormData.model}
                                onChange={handleHRInputChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Serial Number"
                                name="serialNumber"
                                value={hrFormData.serialNumber}
                                onChange={handleHRInputChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Issued Date"
                                name="issuedDate"
                                InputLabelProps={{ shrink: true }}
                                value={hrFormData.issuedDate}
                                onChange={handleHRInputChange}
                                required
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={handleCloseHRDialog}>Cancel</Button>
                    <Button onClick={handleHRSubmit} variant="contained" color="primary">
                        {editingAsset ? 'Update Asset' : 'Add Asset'}
                    </Button>
                </DialogActions>
            </Dialog>

            {isAdmin && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Typography color="textSecondary" gutterBottom>Total Assets</Typography>
                                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.total}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ borderRadius: 3, boxShadow: 2, borderLeft: '5px solid #1976d2' }}>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Typography color="textSecondary" gutterBottom>Issued</Typography>
                                <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                                    {stats.issued}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ borderRadius: 3, boxShadow: 2, borderLeft: '5px solid #2e7d32' }}>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Typography color="textSecondary" gutterBottom>Available/Returned</Typography>
                                <Typography variant="h4" color="success" sx={{ fontWeight: 'bold' }}>
                                    {stats.returned}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ borderRadius: 3, boxShadow: 2, borderLeft: '5px solid #d32f2f' }}>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Typography color="textSecondary" gutterBottom>Damaged</Typography>
                                <Typography variant="h4" color="error" sx={{ fontWeight: 'bold' }}>
                                    {stats.damaged}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {isAdmin ? (
                <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: 3, mb: 6 }}>
                    
                    <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        <TextField
                            label="Search Assets / Employees"
                            variant="outlined"
                            size="small"
                            sx={{ flexGrow: 1, minWidth: '200px' }}
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                        />
                        <TextField
                            select
                            label="Status"
                            size="small"
                            sx={{ minWidth: '150px' }}
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                        >
                            <MenuItem value="All">All Statuses</MenuItem>
                            <MenuItem value="Issued">Issued</MenuItem>
                            <MenuItem value="Returned">Returned</MenuItem>
                            <MenuItem value="Damaged">Damaged</MenuItem>
                        </TextField>
                        <TextField
                            select
                            label="Asset Type"
                            size="small"
                            sx={{ minWidth: '150px' }}
                            value={typeFilter}
                            onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
                        >
                            <MenuItem value="All">All Types</MenuItem>
                            {Object.keys(assetTypeIcons).map(type => (
                                <MenuItem key={type} value={type}>{type}</MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <TableContainer>
                        <Table sx={{ minWidth: 650 }}>
                            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Employee Name</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Employee ID</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Asset Name</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Serial/Model</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Cost Center</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Issued Date</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {allAssets.map((asset) => (
                                    <TableRow key={asset.id} hover>
                                         <TableCell>
                                             {asset.employee 
                                                 ? [
                                                     (asset.employee.firstName && asset.employee.firstName !== 'null') ? asset.employee.firstName : '',
                                                     (asset.employee.lastName && asset.employee.lastName !== 'null') ? asset.employee.lastName : ''
                                                   ].filter(Boolean).join(' ') || 'N/A'
                                                 : 'N/A'}
                                         </TableCell>
                                        <TableCell>{asset.employee_id}</TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}>{asset.assetName}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {assetTypeIcons[asset.assetType] || assetTypeIcons['Other']}
                                                {asset.assetType}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{asset.model}</Typography>
                                            <Typography variant="caption" color="textSecondary">{asset.serialNumber}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            {asset.costCenter ? (
                                                <Chip
                                                    label={`${asset.costCenter.name} (${asset.costCenter.code})`}
                                                    size="small"
                                                    color="info"
                                                    variant="outlined"
                                                />
                                            ) : (
                                                <Typography variant="caption" color="textSecondary">N/A</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={asset.status}
                                                color={getStatusColor(asset.status)}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {new Date(asset.issuedDate).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <IconButton size="small" onClick={() => handleEdit(asset)} color="primary">
                                                    <Edit size={18} />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => handleDelete(asset.id)} color="error">
                                                    <Trash2 size={18} />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {allAssets.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                            No assets found in the organization.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {totalPages >= 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, borderTop: '1px solid #e2e8f0' }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(e, v) => setPage(v)}
                                color="primary"
                            />
                        </Box>
                    )}
                </Paper>
            ) : (
                <Alert severity="error">
                    Access Denied: Only HR and Administrators can view this management page.
                    If you are an employee, please use the "My Assets" page.
                </Alert>
            )}
        </Box>
    );
};

export default AssetManagement;
